import {Component, Module} from 'app/decorators';
import _ from 'lodash';

import ProductRateStore from 'app/stores/product.rate';
import tpl from './products.component.html';


let pintsUnits = ['GAL', 'GALS'];
let convertionGallonsToPints = 8;


@Module({
    name: "app.directives.products.component",
    modules: [
        ProductRateStore,
    ]
})
@Component({
    selector: 'blnd-products',
    bindings: {
        ngModel: '=',
        mode: '@',
        company: '<',
        customer: '<',
        acres: '<',
        crop: '<',
        acresChanged: '<',
        ngChange: '&?',
        errors: '=?',
        showPrices: '<?',
        hasErrors: '=?',
        convertToPints: '<?',
    },
    template: tpl,
})
export default class C {
    constructor ($scope, $element, ProductRateStore) {
        "ngInject";
        let self = this;
        this.scope = $scope;
        this.$element = $element;
        this.productRateStore = ProductRateStore;
        self.showPrices = self.showPrices || false;
        self.convertToPints = self.convertToPints || false;
        self.isDisabled = false;
        // Per-product debounced rate checkers, to avoid flooding /products-rate with a request on every keystroke/rate change.
        this._verifyRateDebounced = {};
    }

    $onInit () {
        let self = this;
        self.watcher = self.scope.$watch(() => self.ngModel, () => {
            if (self.showPrices){
                self.ngModel.forEach(p => self.calcTotalPrice(p));
            }

            self.ngModel.forEach(p => {
                // Set quantityDisplay
                if (!p.quantityDisplay) {
                    p.quantityDisplay = self.getQuantity(p);
                }
            });

            if (self.onChange) {
                self.onChange();
            }
        }, true);
    }

    $doCheck () {
        if (this.ngModel.length >= 10) {
            this.isDisabled = true;
        }
    }

    $onDestroy () {
        _.forEach(this._verifyRateDebounced, (fn) => fn.cancel());
        this._verifyRateDebounced = {};
        if (this.watcher) {
            this.watcher();
            this.watcher = null;
        }
        this.$element.remove();
        this.$element = null;
    }


    $onChanges (changes) {
        if (this.mode === 'view') { return; }
        let self = this;
        if (changes.acres) {
            let acresCurrent = changes.acres.currentValue;
            let isFirst = changes.acres.isFirstChange();

            if (isFirst && acresCurrent === 0) { return; }
            if ((changes.acresChanged || changes.convertToPints) && changes.acres.previousValue.constructor.name !== 'UNINITIALIZED_VALUE') {
                self.ngModel.forEach(p => self.onRateChange(p));
            }
        }

        if (changes.crop && changes.crop.currentValue) {
            self.verifyProductRate();
        }
    }

    hasFieldErrors (index) {
        const err = this.errors && this.errors[index];
        if (!err) return false;
        return !!(err.restricted || err.quantity || err.rate_acre || err.product);
    }

    checkErrors () {
        if (!_.isUndefined(this.hasErrors)) {
            let hasErrors = _.filter(this.ngModel, obj => {
                return obj.errorMessage !== null && parseFloat(obj.rate_acre) !== 0;
            });
            this.hasErrors = hasErrors.length > 0;
        }
    }

    addProduct () {
        if (this.ngModel.length >= 10) {
            this.isDisabled = true;
            return
        }

        // Add default values
        this.ngModel.push({
            rate_acre: 0,
            quantity: 0,
            quantityDisplay: 0,
        });

        this.triggerFocus = true;
    }

    removeProduct (product) {
        let index = this.ngModel.indexOf(product);
        this.ngModel.splice(index, 1);
        this.isDisabled = false;

        try {
            // Remove error if exists
            if (this.errors[index]) {
                delete this.errors[index];
            }
        } catch(error)  {
            // Catch TypeError exception
        }
    }

    onPress (e, isLast) {
        if (e.which === 9 && isLast) {
            this.addProduct();
        }
    }

    onChange () {
        if (this.ngChange) {
            this.ngChange();
        }
    }

    verifyProductRate () {
        let self = this;
        let payload = _.map(this.ngModel, (obj) => {
            return {
                product: obj.product.id,
                crop: this.crop.id,
                rate: obj.rate_acre
            };
        });

        this.productRateStore.checkProducts(payload).then(
            function () {
                _.map(self.ngModel, (obj) => { obj.errorMessage = null; })
                self.checkErrors();
            },
            function (response) {
                _.map(response.data, function(data, idx) {
                    if (data) {
                        self.ngModel[idx].errorMessage = _.first(data.rate) || null;
                    }
                });
                self.checkErrors();
            },
        );
    }

    verifyRate (product) {
        // Debounce per product: rapid rate changes should collapse into a single `/products-rate` request instead of one per keystroke.
        const key = (product.product && product.product.id) || 'unknown';
        if (!this._verifyRateDebounced[key]) {
            this._verifyRateDebounced[key] = _.debounce(
                (p) => this._verifyRate(p),
                400,
            );
        }
        this._verifyRateDebounced[key](product);
    }

    _verifyRate (product) {
        let self = this;

        if (this.crop) {
            this.productRateStore.checkProducts({
                product: product.product.id,
                crop: this.crop.id,
                rate: product.rate_acre
            }).then(
                () => {
                    product.errorMessage = null;
                    self.checkErrors();
                },
                (response) => {
                    product.errorMessage = _.first(_.first(response.data).rate);
                    self.checkErrors();
                },
            );
        }
    }

    calcTotalPrice (obj, itemChanged) {
        if (!this.showPrices || !_.has(obj, 'product')) { return; }
        let div = (pintsUnits.indexOf(obj.product.units.id) >= 0) ? convertionGallonsToPints : 1;

        let priceLevels = _.fromPairs(_.map(obj.product.prices, function (obj) {
            return [obj.price_level, obj.price];
        }));

        if (itemChanged) {
            obj.unit_price = 0;
            obj.total_price = 0;
        }

        // Only recalculate unit_price if it doesn't exist or if item changed (new product)
        // This prevents overwriting existing prices when editing orders
        // Use _.isUndefined() to distinguish between unit_price=0 and unit_price not set
        if (_.isUndefined(obj.unit_price) || itemChanged) {
            // Get unit price from response
            if (_.has(priceLevels, 'special')) {
                obj.isSpecial = true;
                obj.unit_price = priceLevels.special;
            } else {
                obj.isSpecial = false;
                obj.unit_price = (obj.product.prices && obj.product.prices.length > 0) ? priceLevels[this.customer.price_level] : obj.unit_price;
            }
        }

        // Display value
        obj.unitPrice = _.round((obj.unit_price || 0.00) / div , 3);

        // Total price for product - recalculate based on current quantity and EXISTING unit_price
        obj.total_price = (obj.quantityDisplay) ? _.round(obj.quantityDisplay * obj.unit_price / div, 2) : _.round(obj.quantity * obj.unit_price, 2);
        obj.total_price = obj.total_price || 0;
    }

    onProductChange (product) {
        if (!this.showPrices) { return; }
        this.calcTotalPrice(product, true);
    }

    canConvertToPints(product) {
        return this.convertToPints && product.product && product.product.units && pintsUnits.indexOf(product.product.units.name) >= 0;
    }

    getUnit (product) {
        if (this.canConvertToPints(product)) {
            return 'PINTS';
        }

        try {
            return product.product.units.name;
        } catch (err) {
            return '';
        }
    }

    getQuantity (product) {
        if (this.canConvertToPints(product)) {
            return _.round(product.quantity * convertionGallonsToPints, 2);
        }
        return product.quantity;
    }

    hasWarnings (product) {
        if (!product.product) return false;
        return !product.product.is_active ||
            (this.showPrices && this.getUnitPrice(product) === 0);
    }

    getUnitPrice (product) {
        return product.unitPrice;
    }

    getMaxQuantity (product) {
        if (!product.product || !product.product.units) {
            return undefined;
        }
        let units = product.product.units.id || product.product.units.name;
        if (units === 'GAL' || units === 'GALS') {
            return this.canConvertToPints(product) ? 1200 * convertionGallonsToPints : 1200;
        }
        if (units === 'GLS') {
            return 9999;
        }
        return undefined;
    }

    checkMaxQuantity (product) {
        const maxQty = this.getMaxQuantity(product);
        if (maxQty !== undefined && parseFloat(product.quantityDisplay) > maxQty) {
            product.errorMessage = `Quantity cannot exceed ${maxQty}`;
            this.checkErrors();
            return true;
        }
        return false;
    }

    onRateChange (product) {
        let self = this;
        if (!product.product) { return; }
        if (_.isUndefined(this.acres)) { return; }
        let div = (pintsUnits.indexOf(product.product.units.id) >= 0) ? convertionGallonsToPints : 1;

        if (parseFloat(this.acres) !== 0) {
            // value which will be send to server
            product.quantity = _.round(this.acres * product.rate_acre / div, 3) || 0;
            // value which is visible on UI
            product.quantityDisplay = (self.canConvertToPints(product)) ? _.round(this.acres * product.rate_acre, 2): product.quantity;
        }

        if (this.showPrices) {
            this.calcTotalPrice(product);
        }

        if (this.checkMaxQuantity(product)) { return; }

        // Check product rate
        // Do not check rate in $watch it makes a lot of requests to backend!
        this.verifyRate(product);
    }

    onQuantityChange (product) {
        if (!product.product) { return; }
        if (_.isUndefined(this.acres)) { return; }
        let div = 1;
        if (!this.canConvertToPints(product)) {
            div = (pintsUnits.indexOf(product.product.units.name) >= 0) ? convertionGallonsToPints: 1;
        }
        product.rate_acre = _.round(product.quantityDisplay / this.acres * div, 2) || 0;
        product.quantity = this.canConvertToPints(product) ? _.round(product.quantityDisplay / convertionGallonsToPints, 3) : product.quantityDisplay;

        if (this.showPrices) {
            this.calcTotalPrice(product);
        }

        if (this.checkMaxQuantity(product)) { return; }

        // Check product rate
        // Do not check rate in $watch it makes a lot of requests to backend!
        this.verifyRate(product);
    }
}



// WEBPACK FOOTER //
// ./src/app/directives/products.component.js